import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TableContainer,
  Avatar,
  Stack
} from '@mui/material';
import {
  History,
  CalendarToday,
  Person,
  PlayArrow,
  Description,
  Computer,
  CheckCircle,
  Error,
  Refresh,
  Login,
  Logout,
  Edit,
  Delete,
  Add,
  Visibility,
  Settings,
  Security,
  Warning
} from '@mui/icons-material';

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/audit-logs', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erreur de chargement');
      
      const data = await response.json();
      setLogs(data.logs || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir l'icône selon l'action
  const getActionIcon = (action) => {
    const actionLower = (action || '').toLowerCase();
    
    if (actionLower.includes('login') || actionLower.includes('connexion')) {
      return <Login fontSize="small" color="success" />;
    }
    if (actionLower.includes('logout') || actionLower.includes('déconnexion')) {
      return <Logout fontSize="small" color="action" />;
    }
    if (actionLower.includes('create') || actionLower.includes('créer') || actionLower.includes('add')) {
      return <Add fontSize="small" color="primary" />;
    }
    if (actionLower.includes('update') || actionLower.includes('edit') || actionLower.includes('modif')) {
      return <Edit fontSize="small" color="info" />;
    }
    if (actionLower.includes('delete') || actionLower.includes('suppr')) {
      return <Delete fontSize="small" color="error" />;
    }
    if (actionLower.includes('view') || actionLower.includes('voir') || actionLower.includes('consult')) {
      return <Visibility fontSize="small" color="action" />;
    }
    if (actionLower.includes('settings') || actionLower.includes('paramètre')) {
      return <Settings fontSize="small" color="warning" />;
    }
    
    return <PlayArrow fontSize="small" color="action" />;
  };

  // Fonction pour obtenir la couleur du chip selon l'action
  const getActionColor = (action) => {
    const actionLower = (action || '').toLowerCase();
    
    if (actionLower.includes('login') || actionLower.includes('connexion')) {
      return 'success';
    }
    if (actionLower.includes('delete') || actionLower.includes('suppr')) {
      return 'error';
    }
    if (actionLower.includes('create') || actionLower.includes('créer') || actionLower.includes('add')) {
      return 'primary';
    }
    if (actionLower.includes('update') || actionLower.includes('edit') || actionLower.includes('modif')) {
      return 'info';
    }
    
    return 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <History />
          </Avatar>
          <Typography variant="h4">
            Historique des accès système
          </Typography>
        </Stack>
        <Tooltip title="Actualiser">
          <IconButton onClick={fetchLogs} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      
      {logs.length === 0 ? (
        <Alert severity="info" icon={<History />}>
          Aucun historique d'accès disponible pour le moment
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarToday fontSize="small" />
                    <span>Date/Heure</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Person fontSize="small" />
                    <span>Utilisateur</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PlayArrow fontSize="small" />
                    <span>Action</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Description fontSize="small" />
                    <span>Détails</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Computer fontSize="small" />
                    <span>Adresse IP</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckCircle fontSize="small" />
                    <span>Statut</span>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log, index) => (
                <TableRow 
                  key={log.id} 
                  hover
                  sx={{ 
                    '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(log.createdAt).toLocaleDateString('fr-FR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(log.createdAt).toLocaleTimeString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar 
                        sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.875rem' }}
                      >
                        {(log.userName || log.userEmail || '?')[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {log.userName || 'Utilisateur'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.userEmail}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getActionIcon(log.action)}
                      label={log.action}
                      color={getActionColor(log.action)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.details || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Adresse IP de l'utilisateur">
                      <Chip 
                        icon={<Computer fontSize="small" />}
                        label={log.ipAddress || 'Non disponible'} 
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={log.status === 'success' ? <CheckCircle /> : <Error />}
                      label={log.status === 'success' ? 'Succès' : 'Échec'} 
                      color={log.status === 'success' ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {logs.length} enregistrement(s) au total
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Dernière mise à jour : {new Date().toLocaleString('fr-FR')}
        </Typography>
      </Box>
    </Container>
  );
}